// ai_agent/api_client.js
// ============================================================
// KWERY.AI — Centralised API client
// All endpoints mapped by role. Used by InlineActionForm
// and the AI orchestrator frontend bridge.
// ============================================================

// Empty BASE so requests go through React dev proxy (avoids CORS)
const BASE = process.env.REACT_APP_API_URL || "";

const headers = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// JSON request
const req = async (method, path, body) => {
  const opts = { method, headers: headers() };
  if (body) opts.body = JSON.stringify(body);
  const res = await fetch(`${BASE}${path}`, opts);
  if (!res.ok) throw new Error(await res.text() || `${res.status}`);
  return res.json().catch(() => ({}));
};

// FormData request — for FastAPI Form(...) endpoints
const reqForm = async (method, path, body) => {
  const fd = new FormData();
  Object.entries(body).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") fd.append(k, v);
  });
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    body: fd,
  });
  if (!res.ok) throw new Error(await res.text() || `${res.status}`);
  return res.json().catch(() => ({}));
};

const get   = (path)        => req("GET",    path);
const post  = (path, body)  => req("POST",   path, body);
const postF = (path, body)  => reqForm("POST", path, body);  // FormData
const patch = (path, body)  => req("PATCH",  path, body);
const del   = (path)        => req("DELETE", path);

// ============================================================
// STAFF APIs
// ============================================================
export const Staff = {
  // Attendance
  getAttendanceCalendar : ()     => get("/api/attendance/calendar"),
  getAttendanceStats    : ()     => {
    // /stats requires start + end query params; default to current month-to-date.
    const today = new Date();
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    const fmt = (d) => d.toISOString().split("T")[0];
    return get(`/api/attendance/stats?start=${fmt(first)}&end=${fmt(today)}`);
  },
  getAttendanceHistory  : ()     => get("/api/attendance/history"),
  regulariseAttendance  : (body) => postF("/api/attendance/regularise", body),

  // Leave
  submitLeave    : (body)  => postF("/api/leave/submit", body),  // FastAPI Form(...)
  getMyLeave     : ()      => get("/api/leave/mine"),
  getLeaveHistory: ()      => get("/api/leave/history"),
  getLeaveBalance: ()      => get("/api/leave/balance"),
  getLeaveBalances: ()     => get("/api/leave/balances"),
  cancelLeave    : (id)    => del(`/api/leave/${id}`),

  // Payroll
  getPayslips      : ()    => get("/api/payroll/payslips"),
  getPayslipByMonth: (m)   => get(`/api/payroll/payslips/by-month?month=${m}`),
  getPayrollMine   : ()    => get("/api/payroll/mine"),
  getPayslipPdf    : (id)  => get(`/api/payroll/payslips/${id}/pdf`),
  getIncrements    : ()    => get("/api/payroll/increments"),
  getCtc           : ()    => get("/api/payroll/ctc"),
  getForm16        : ()    => get("/api/payroll/form16"),
  requestIncomeCert: (body)=> postF("/api/payroll/income-certificate", body),
  requestCorrection: (body)=> postF("/api/payroll/correction", body),
  submitTaxDecl    : (body)=> post("/api/payroll/tax-declaration", body),

  // Performance
  getPerfSummary  : ()     => get("/api/performance/summary"),
  getGoals        : ()     => get("/api/performance/goals"),
  createGoal      : (body) => post("/api/performance/goals", body),
  updateGoal      : (id, body) => patch(`/api/performance/goals/${id}`, body),
  submitSelfReview: (body) => post("/api/performance/self-review", body),
  submitPeerFeedback: (uid, body) => post(`/api/performance/peer-feedback/${uid}`, body),
  getPeerFeedback : ()     => get("/api/performance/peer-feedback"),
  getPerfHistory  : ()     => get("/api/performance/history"),

  // Tickets
  submitTicket    : (body) => postF("/api/tickets/submit", body),
  getTickets      : ()     => get("/api/tickets/list"),
  getTicket       : (id)   => get(`/api/tickets/${id}`),
  getTicketThread : (id)   => get(`/api/tickets/thread/${id}`),
  replyTicket     : (id, body) => post(`/api/tickets/reply/${id}`, body),
  getTicketSummary: ()     => get("/api/tickets/summary"),

  // Profile
  getProfile      : ()     => get("/api/profile/me"),
  updateProfile   : (body) => patch("/api/profile/update-field", body),
  uploadAvatar    : (body) => post("/api/profile/upload-avatar", body),
  downloadProfilePdf: ()   => get("/api/profile/download-pdf"),

  // Learning
  getCourses      : ()     => get("/api/learning/api/learning/courses"),
  getAssigned     : ()     => get("/api/learning/api/learning/assigned"),
  getLearningHistory: ()   => get("/api/learning/api/learning/history"),
};

// ============================================================
// MANAGER APIs (team-level)
// ============================================================
export const Manager = {
  getTeam             : ()     => get("/api/manager/team"),
  getEmployeeWork     : (uid)  => get(`/api/manager/employee/${uid}/work`),
  getAttendanceDaily  : ()     => get("/api/manager/attendance/daily"),
  getAttendanceSummary: ()     => get("/api/manager/attendance/summary"),
  getAttendanceExceptions: ()  => get("/api/manager/attendance/exceptions"),
  getLeaveRange       : ()     => get("/api/manager/leave/range"),
  getLeaveApprovals   : ()     => get("/api/manager/leave/approvals"),
  approveLeave        : (id)   => post(`/api/leave/${id}/approve`, {}),
  rejectLeave         : (id, body) => post(`/api/leave/${id}/reject`, body),
  getPerformance      : ()     => get("/api/manager/performance"),
  getPerfQueue        : ()     => get("/api/manager/performance/queue"),
  submitManagerReview : (uid, body) => post(`/api/performance/manager-review/${uid}`, body),
  approveTicket       : (id)   => post(`/api/tickets/approvals/${id}/approve`, {}),
  rejectTicket        : (id, body) => post(`/api/tickets/approvals/${id}/reject`, body),
  getTeamPayroll      : ()     => get("/api/payroll/team"),
};

// ============================================================
// ADMIN APIs (org-wide)
// ============================================================
export const Admin = {
  // Users
  getUsers       : ()      => get("/api/admin/users/"),
  getUser        : (id)    => get(`/api/admin/users/${id}`),
  createUser     : (body)  => post("/api/admin/users/", body),
  updateUser     : (id, b) => patch(`/api/admin/users/${id}`, b),
  deleteUser     : (id)    => del(`/api/admin/users/${id}`),

  // Leave admin
  getLeaveQueue  : ()      => get("/api/admin/leave-rules/queue"),
  getLeaveKpis   : ()      => get("/api/admin/leave-rules/kpis"),
  getLeaveHeatmap: ()      => get("/api/admin/leave-rules/heatmap"),
  getStaffingRisk: ()      => get("/api/admin/leave-rules/staffingrisk"),
  approveLeave   : (id)    => post(`/api/admin/leave-rules/request/${id}/approve`, {}),
  rejectLeave    : (id, b) => post(`/api/admin/leave-rules/request/${id}/reject`, b),
  overrideLeave  : (id, b) => post(`/api/admin/leave-rules/request/${id}/override`, b),
  adjustBalance  : (body)  => post("/api/admin/leave-rules/balances/adjust", body),

  // Attendance admin
  getAttendanceKpis    : () => get("/api/admin/attendance/kpis"),
  getAttendanceAnomalies: () => get("/api/admin/attendance/anomalies"),
  getRegularisations   : () => get("/api/admin/attendance/regularisations"),
  approveReg  : (id)        => post(`/api/admin/attendance/regularisations/${id}/approve`, {}),
  rejectReg   : (id, b)     => post(`/api/admin/attendance/regularisations/${id}/reject`, b),
  getOvertime : ()           => get("/api/admin/attendance/overtime"),
  approveOT   : (id)        => post(`/api/admin/attendance/overtime/${id}/approve`, {}),
  rejectOT    : (id, b)     => post(`/api/admin/attendance/overtime/${id}/reject`, b),

  // Tickets admin
  getTickets    : ()         => get("/api/admin/tickets/list"),
  getTicketKpis : ()         => get("/api/admin/tickets/kpis"),
  assignTicket  : (id, b)    => post(`/api/admin/tickets/assign/${id}`, b),
  setTicketStatus: (id, b)   => post(`/api/admin/tickets/status/${id}`, b),
  escalateTicket: (id, b)    => post(`/api/admin/tickets/escalate/${id}`, b),
  approveTicket : (id)       => post(`/api/admin/tickets/approve/${id}`, {}),

  // Payroll admin
  getPayrollSummary : ()     => get("/api/admin/payroll/run/summary"),
  getPayrollExceptions: ()   => get("/api/admin/payroll/exceptions"),
  resolveException  : (id)   => post(`/api/admin/payroll/exceptions/${id}/resolve`, {}),
  lockPayroll       : (b)    => post("/api/admin/payroll/run/lock", b),
  approvePayroll    : (b)    => post("/api/admin/payroll/run/approve", b),
  recalcPayroll     : (b)    => post("/api/admin/payroll/run/recalc", b),
  generatePayslips  : (b)    => post("/api/admin/payroll/run/generate-payslips", b),
  setEmployeeCtc    : (uid, b) => post(`/api/admin/payroll/employee/${uid}/ctc`, b),
  addAdjustment     : (uid, b) => post(`/api/admin/payroll/employee/${uid}/adjustments`, b),
  getCostByDept     : ()     => get("/api/admin/payroll/analytics/cost-by-dept"),
  getPayrollTrend   : ()     => get("/api/admin/payroll/analytics/trend"),

  // Performance admin
  getPerfKpis   : ()         => get("/api/admin/performance/kpis"),
  startCycle    : (b)        => post("/api/admin/performance/cycle/start", b),
  lockCycle     : (id)       => post(`/api/admin/performance/cycle/${id}/lock`, {}),
  closeCycle    : (id)       => post(`/api/admin/performance/cycle/${id}/close`, {}),
  getReviews    : ()         => get("/api/admin/performance/reviews"),
};

// ============================================================
// EXECUTIVE APIs (read-only strategic)
// ============================================================
export const Executive = {
  getDashboard   : ()  => get("/api/executive/dashboard"),
  getCostByDept  : ()  => get("/api/admin/payroll/analytics/cost-by-dept"),
  getPayrollTrend: ()  => get("/api/admin/payroll/analytics/trend"),
  getAttendanceTrends: () => get("/api/admin/attendance/trends"),
  getAttendanceKpis:  () => get("/api/admin/attendance/kpis"),
  getPerfKpis    : ()  => get("/api/admin/performance/kpis"),
  getLeaveHeatmap: ()  => get("/api/admin/leave-rules/heatmap"),
  getStaffingRisk: ()  => get("/api/admin/leave-rules/staffingrisk"),
  getLeaveKpis   : ()  => get("/api/admin/leave-rules/kpis"),
};

// ============================================================
// ROLE-AWARE API SELECTOR
// Returns the right API set based on user role
// ============================================================
export const getApiForRole = (role) => {
  switch (role) {
    case "executive":  return { ...Staff, ...Executive };
    case "admin":      return { ...Staff, ...Manager, ...Admin };
    case "manager":    return { ...Staff, ...Manager };
    default:           return Staff;
  }
};

export default { Staff, Manager, Admin, Executive, getApiForRole };